/* iter55f92 — StoryBibleSummary card.
 *
 * Extracted from `CharacterBiblePanel.jsx` to keep that panel under
 * 500 lines. Renders the collapsible "Story Bible" summary that
 * appears above the character roster when a project has an imported
 * three-act synopsis / manuscript.
 *
 * iter-series-bible · Feb-2026 — When the parent passes a
 * `seriesBibleId`, we fetch the matching row from
 * `/api/series-bibles/{id}` and render a small pill above the
 * summary body so authors always know which shared canon their
 * manuscript is attached to. Clicking the pill expands the full
 * bible markdown inside the same collapsible.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ScrollText, ChevronRight, Maximize2, Library } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function StoryBibleSummary({ bible, open, onToggle, onOpenDialog, seriesBibleId }) {
  const [series, setSeries]           = useState(null);
  const [seriesOpen, setSeriesOpen]   = useState(false);
  useEffect(() => {
    let alive = true;
    if (!seriesBibleId) { setSeries(null); return; }
    http.get(`/series-bibles/${seriesBibleId}`)
      .then((r) => { if (alive) setSeries(r.data); })
      .catch(() => { if (alive) setSeries(null); });
    return () => { alive = false; };
  }, [seriesBibleId]);

  if (!bible && !seriesBibleId) return null;
  return (
    <div className="shrink-0 border-b border-panel-border bg-secondary/20" data-testid="story-bible-card">
      {/* Series Bible pill — always visible when the project attaches one. */}
      {seriesBibleId && (
        <div className="border-b border-panel-border bg-primary/5 px-3 py-1.5" data-testid="series-bible-pill">
          <button
            type="button"
            onClick={() => setSeriesOpen((v) => !v)}
            className="flex w-full items-center justify-between gap-2 text-left hover:opacity-90"
            data-testid="series-bible-toggle"
          >
            <div className="flex items-center gap-2">
              <Library className="h-3.5 w-3.5 text-primary" />
              <span className="text-[13px] font-semibold uppercase tracking-wider text-primary">
                Series canon
              </span>
              {series ? (
                <>
                  <span className="text-[13.5px] font-semibold text-foreground">{series.title}</span>
                  <Badge variant="outline" className="h-4 px-1.5 text-[9px] uppercase tracking-wider" data-testid="series-bible-version">
                    {series.version}
                  </Badge>
                </>
              ) : (
                <span className="text-[13px] text-muted-foreground">{seriesBibleId}</span>
              )}
            </div>
            <ChevronRight className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', seriesOpen && 'rotate-90')} />
          </button>
          {seriesOpen && series && (
            <pre
              data-testid="series-bible-markdown"
              className="scroll-thin mt-2 max-h-[280px] overflow-auto whitespace-pre-wrap rounded-md border border-primary/20 bg-background p-2 font-serif text-[13px] leading-relaxed"
            >
              {series.markdown}
            </pre>
          )}
        </div>
      )}
      {bible && (<>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onToggle}
          className="flex flex-1 items-center justify-between gap-2 px-3 py-2 text-left hover:bg-secondary/40"
          data-testid="story-bible-toggle"
          aria-expanded={open}
        >
          <div className="flex items-center gap-2">
            <ScrollText className="h-3.5 w-3.5 text-primary" />
            <span className="text-[13.5px] font-semibold">Story Bible</span>
            {bible.claude_parsed && (
              <Badge variant="outline" className="h-4 px-1.5 text-[9px] uppercase tracking-wider">Claude</Badge>
            )}
          </div>
          <ChevronRight className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', open && 'rotate-90')} />
        </button>
        {/* iter55z-j — Explicit "Open in dialog" for a wider, scrollable read */}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-[13px]"
          onClick={onOpenDialog}
          data-testid="story-bible-open-dialog"
          title="Open Story Bible in a larger scrollable view"
        >
          <Maximize2 className="h-3 w-3" />
          Open
        </Button>
      </div>
      </>)}
      {open && (
        <div
          className="scroll-thin max-h-[280px] space-y-2 overflow-y-auto px-3 pb-3 pt-1 text-[13.5px] leading-relaxed"
          data-testid="story-bible-inline-scroll"
        >
          {bible.story_arc && (
            <div data-testid="story-bible-arc">
              <div className="mb-0.5 text-[13px] uppercase tracking-wider text-foreground/70">Story arc</div>
              <p className="text-foreground/90">{bible.story_arc}</p>
            </div>
          )}
          {bible.mood && (
            <div data-testid="story-bible-mood">
              <div className="mb-0.5 text-[13px] uppercase tracking-wider text-muted-foreground">Mood</div>
              <p className="italic text-foreground/80">{bible.mood}</p>
            </div>
          )}
          {Array.isArray(bible.themes) && bible.themes.length > 0 && (
            <div data-testid="story-bible-themes">
              <div className="mb-1 text-[13px] uppercase tracking-wider text-muted-foreground">Themes</div>
              <div className="flex flex-wrap gap-1">
                {bible.themes.map((t) => (
                  <Badge key={t} variant="secondary" className="h-5 px-1.5 text-[13px] font-normal">{t}</Badge>
                ))}
              </div>
            </div>
          )}
          {bible.worldbuilding && (
            <div data-testid="story-bible-worldbuilding">
              <div className="mb-0.5 text-[13px] uppercase tracking-wider text-muted-foreground">Worldbuilding</div>
              <p className="text-foreground/85">{bible.worldbuilding}</p>
            </div>
          )}
          {bible.imported_at && (
            <div className="pt-1 text-[13px] text-foreground/70">
              Imported {new Date(bible.imported_at).toLocaleString()} · {bible.word_count || '—'} words
            </div>
          )}
        </div>
      )}
    </div>
  );
}
